import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, FormControl, InputLabel, Select, MenuItem, Box, Typography,Grid } from '@mui/material';
import dayjs from 'dayjs';
import { BASE_URL } from "../../../../../utils";

export const EmployeeAttendance = () => {
  const [attendance, setAttendance] = useState({});
  const [month, setMonth] = useState(dayjs().month()); // Start with the current month
  const [year, setYear] = useState(dayjs().year());  // Start with the current year
  const [employeesData, setEmployeesData] = useState([]);
  const [allPresentForDate, setAllPresentForDate] = useState({});

  // Fetch Employees Data
  useEffect(() => {
    const fetchEmployeesData = async () => {
      try {
        const response = await fetch(`${BASE_URL}/employee/list`); // Endpoint to fetch employees
        const data = await response.json();
        setEmployeesData(data.data);
      } catch (error) {
        console.error("Error fetching employees data:", error);
      }
    };

    fetchEmployeesData();
  }, []);

  // Fetch Attendance Data
  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        const promises = employeesData.map(async (employee) => {
          const response = await fetch(`${BASE_URL}/attendance/${employee._id}/${year}/${month + 1}`);
          const data = await response.json();
          return { employeeId: employee._id, attendanceData: data };
        });

        const attendanceResults = await Promise.all(promises);
        const newAttendance = {};
        attendanceResults.forEach(({ employeeId, attendanceData }) => {
          newAttendance[employeeId] = {};
          attendanceData.forEach((entry) => {
            const date = dayjs(entry.date);
            newAttendance[employeeId][date.date()] = entry.status === 'Present';
          });
        });

        setAttendance(newAttendance);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
      }
    };

    if (employeesData.length > 0) {
      fetchAttendanceData();
    }
  }, [month, year, employeesData]);

  // Toggle Attendance for Employee
  const toggleAttendanceForEmployee = async (employeeId, date) => {
    const newAttendance = { ...attendance };

    // Ensure attendance for the employee and date is initialized
    if (!newAttendance[employeeId]) {
      newAttendance[employeeId] = {};
    }

    // Toggle attendance for the specific date
    newAttendance[employeeId][date] = !newAttendance[employeeId][date]; // Toggle present/absent
    setAttendance(newAttendance);

    const status = newAttendance[employeeId][date] ? 'Present' : 'Absent';
    const fullDate = dayjs(`${year}-${month + 1}-${date}`).format('YYYY-MM-DD');
    try {
      await fetch(`${BASE_URL}/attendance/${employeeId}/${fullDate}`, {
        method: 'POST',
        body: JSON.stringify({ status }),
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      console.error('Error updating individual attendance:', error);
    }
  };

  // Toggle All Attendance for Date
  const toggleAllAttendanceForDate = async (date) => {
    const newAllPresentForDate = { ...allPresentForDate };
  
    // Ensure allPresentForDate for the date is initialized
    if (!newAllPresentForDate[date]) {
      newAllPresentForDate[date] = false;
    }
  
    let newAttendance = { ...attendance };  // Copy the existing attendance state to preserve other dates' data
  
    if (newAllPresentForDate[date]) {
      // If already all present, mark all as absent for the specific date
      employeesData.forEach((employee) => {
        newAttendance[employee._id] = {
          ...newAttendance[employee._id],  // Preserve attendance for other dates
          [date]: false                    // Mark the toggled date as absent
        };
      });
      newAllPresentForDate[date] = false; // Reset to all absent
    } else {
      // If not all present, mark all as present for the specific date
      employeesData.forEach((employee) => {
        newAttendance[employee._id] = {
          ...newAttendance[employee._id],  // Preserve attendance for other dates
          [date]: true                     // Mark the toggled date as present
        };
      });
      newAllPresentForDate[date] = true; // Set to all present
    }
  
    setAttendance(newAttendance);  // Update the state with the new attendance data
    setAllPresentForDate(newAllPresentForDate);  // Update the "all present" state
  
    const status = newAllPresentForDate[date] ? 'Present' : 'Absent';
    const fullDate = dayjs(`${year}-${month + 1}-${date}`).format('YYYY-MM-DD');
    
    try {
      // Send bulk update request to the backend
      await fetch(`${BASE_URL}/attendance/bulk/${fullDate}`, {
        method: 'POST',
        body: JSON.stringify({ status }),
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      console.error('Error updating bulk attendance:', error);
    }
  };
  

  // Generate Dates for the Current Month
  const generateDates = (month) => {
    const daysInMonth = dayjs().month(month).daysInMonth();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  const dates = generateDates(month);

  return (
    <Box sx={{ padding: 3, backgroundColor: 'white', borderRadius: 2, height: "100vh", boxSizing: "border-box" }}>
      <Typography variant="h3" color="primary" sx={{ fontWeight: 600 , marginBottom:"2%"}}>
        Employee Attendance
      </Typography>

      <Grid container spacing={2}>
  {/* Year Selector */}
  <Grid item xs={6}>
    <FormControl fullWidth sx={{ fontSize: "18px" }}>
      <InputLabel sx={{ fontSize: "18px" }}>Year</InputLabel>
      <Select value={year} onChange={(e) => setYear(e.target.value)} label="Year" sx={{ fontSize: "16px" ,fontWeight:700}}>
        {Array.from({ length: 10 }, (_, index) => dayjs().year() - 5 + index).map((yearValue) => (
          <MenuItem sx={{fontSize:"12px",fontWeight:700}}key={yearValue} value={yearValue}>{yearValue}</MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>

  {/* Month Selector */}
  <Grid item xs={6}>
    <FormControl fullWidth sx={{ fontSize: "18px" }}>
      <InputLabel sx={{ fontSize: "18px" }}>Month</InputLabel>
      <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Month" sx={{ fontSize: "16px",fontWeight:700 }}>
        {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((monthName, index) => (
          <MenuItem sx={{fontSize:"12px",fontWeight:700}} key={monthName} value={index}>{monthName}</MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
</Grid>

      {/* Attendance Table */}
      <TableContainer sx={{ maxHeight: '500px', overflowY: 'auto', position: 'relative' }}>
  <Table sx={{ minWidth: 650 }}>
    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
      <TableRow>
        <TableCell sx={{ fontSize: "16px", fontWeight: "bold" , width: "20%", whiteSpace: "nowrap"}}>Employee Name</TableCell>
        {dates.map((date) => (
          <TableCell key={date} align="center" sx={{ fontSize: "14px" }}>
            {date}
            <Button
              onClick={() => toggleAllAttendanceForDate(date)}
              size="small"
              sx={{ marginLeft: 0, borderRadius: "50px", fontSize: "12px", padding: "0px" }}
              color={allPresentForDate[date] ? 'success' : 'error'}
            >
              {allPresentForDate[date] ? 'Mark All Absent' : 'Mark All Present'}
            </Button>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {employeesData.map((employee) => (
        <TableRow key={employee._id}>
          <TableCell sx={{ fontSize: "14px", width: "20%", whiteSpace: "nowrap" ,fontWeight:700}}>
            {employee.personalInfo.firstName} {employee.personalInfo.lastName}
          </TableCell>
          {dates.map((date) => (
            <TableCell key={date} align="center" sx={{ fontSize: "14px" }}>
              <Button
                variant="outlined"
                color={attendance[employee._id]?.[date] ? 'success' : 'error'}
                onClick={() => toggleAttendanceForEmployee(employee._id, date)}
                sx={{
                  fontSize: "14px",
                  minWidth: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  padding: "0"
                }}
              >
                {attendance[employee._id]?.[date] ? 'P' : 'A'}
              </Button>
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>


<style>
  {`
    /* Horizontal scrollbar styling */
    .MuiTableContainer-root::-webkit-scrollbar {
      height: 6px; /* Adjust height for horizontal scrollbar */
    }

    .MuiTableContainer-root::-webkit-scrollbar-track {
      background: #f1f1f1; /* Background color of the track */
    }

    .MuiTableContainer-root::-webkit-scrollbar-thumb {
      background: #888; /* Color of the scrollbar thumb */
      border-radius: 10px;
    }

    .MuiTableContainer-root::-webkit-scrollbar-thumb:hover {
      background: #555; /* Hover color for the scrollbar thumb */
    }
  `}
</style>


    </Box>
  );
};
